import { useMemo } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { useOrganizationStore } from "../stores/useOrganizationStore";

// Hover reason for controls that start something new (e.g. running a
// simulation), disabled while viewing a deactivated facility.
export const DEACTIVATED_FACILITY_SIMULATION_REASON =
  "This facility is deactivated, so simulations can't be run.";

export interface ActiveFacilityDeactivation {
  isDeactivated: boolean;
  // ISO 8601 instant the facility was deactivated, when known.
  deactivatedAt: string | null;
  // The facility's IANA timezone, for formatting deactivatedAt as a date.
  timezone: string | undefined;
}

/**
 * Whether a facility is deactivated (JAA-1117), from the organizations list.
 *
 * Only Joules platform admins ever see a deactivated facility here: GET
 * /organizations returns deactivated facilities to them alone (view-only
 * access), and everyone else hits the backend's "Facility is deactivated"
 * 403 instead (see isFacilityDeactivatedError). So `isDeactivated` true means
 * "a platform admin is viewing a deactivated facility's history".
 */
export function useFacilityDeactivation(facilityId: number): ActiveFacilityDeactivation {
  const organizations = useOrganizationStore((s) => s.organizations);

  return useMemo(() => {
    const facility = organizations
      ?.flatMap((org) => org.facilities)
      .find((f) => f.id === facilityId);
    const isDeactivated = facility?.status === "deactivated";
    return {
      isDeactivated,
      deactivatedAt: isDeactivated ? (facility?.deactivatedAt ?? null) : null,
      timezone: facility?.timezone,
    };
  }, [organizations, facilityId]);
}

/** useFacilityDeactivation for the currently selected facility. */
export function useActiveFacilityDeactivation(): ActiveFacilityDeactivation {
  const activeFacilityId = useOrganizationStore((s) => s.activeFacilityId);
  return useFacilityDeactivation(activeFacilityId);
}

/**
 * The last moment the active facility has data for: the end of its
 * deactivation day while deactivated, otherwise null (data runs up to now).
 * For charts whose default ranges end "now" rather than coming from
 * useFacilityConfig, which applies the same cap itself.
 */
export function useFacilityDataEnd(): Dayjs | null {
  const { deactivatedAt } = useActiveFacilityDeactivation();
  return useMemo(() => {
    if (!deactivatedAt) return null;
    const end = dayjs(deactivatedAt).endOf("day");
    return end.isValid() ? end : null;
  }, [deactivatedAt]);
}
